import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faFlag,
  faChevronLeft,
  faChevronRight,
  faTimes,
  faUpload,
  faFileAlt,
  faTrash,
  faCalculator,
  faGripVertical,
  faPause,
  faPlay,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../../Components/Common/Modal";
import { useEffect, useState, useRef } from "react";
import Button from "../../../../Components/Common/Button";
import useSWRMutation from "swr/mutation";
import useSWR from "swr";
import { modelTestQuestionUrls } from "../../../../Api/Auth/ApiUrls";
import { sendPutRequestAuth } from "../../../../Api/swrConfig";
import { useAuth } from "../../../../Context/AuthContext";
import PDFViewer from "../../../../Components/Common/PDFReader";

// Floating Calculator Component (unchanged)
const FloatingCalculator = ({ isVisible, onClose }) => {
  const [display, setDisplay] = useState("0");
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForNewValue, setWaitingForNewValue] = useState(false);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const calculatorRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.target.closest(".calc-button") || e.target.closest(".calc-close"))
      return;

    const rect = calculatorRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    setPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const inputNumber = (num) => {
    if (waitingForNewValue) {
      setDisplay(String(num));
      setWaitingForNewValue(false);
    } else {
      setDisplay(display === "0" ? String(num) : display + num);
    }
  };

  const inputDecimal = () => {
    if (waitingForNewValue) {
      setDisplay("0.");
      setWaitingForNewValue(false);
    } else if (display.indexOf(".") === -1) {
      setDisplay(display + ".");
    }
  };

  const clear = () => {
    setDisplay("0");
    setPreviousValue(null);
    setOperation(null);
    setWaitingForNewValue(false);
  };

  const performOperation = (nextOperation) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      const newValue = calculate(currentValue, inputValue, operation);

      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }

    setWaitingForNewValue(true);
    setOperation(nextOperation);
  };

  const calculate = (firstValue, secondValue, operation) => {
    switch (operation) {
      case "+":
        return firstValue + secondValue;
      case "-":
        return firstValue - secondValue;
      case "×":
        return firstValue * secondValue;
      case "÷":
        return firstValue / secondValue;
      case "=":
        return secondValue;
      default:
        return secondValue;
    }
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);
    if (previousValue !== null && operation) {
      const newValue = calculate(previousValue, inputValue, operation);
      setDisplay(String(newValue));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForNewValue(true);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      ref={calculatorRef}
      className="fixed bg-white border-2 border-gray-300 rounded-lg shadow-2xl z-[10000] select-none"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? "grabbing" : "grab",
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <div className="flex items-center justify-between bg-gray-100 p-2 rounded-t-lg">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faGripVertical} className="text-gray-400" />
          <span className="text-sm font-medium">Calculator</span>
        </div>
        <button
          className="calc-close text-gray-500 hover:text-red-600 w-6 h-6 flex items-center justify-center"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} size="sm" />
        </button>
      </div>

      {/* Display */}
      <div className="p-4 pb-2">
        <div className="bg-gray-900 text-white text-right p-3 rounded text-xl font-mono min-h-[50px] flex items-center justify-end">
          {display}
        </div>
      </div>

      {/* Buttons */}
      <div className="p-4 pt-2">
        <div className="grid grid-cols-4 gap-2">
          <button
            className="calc-button bg-gray-200 hover:bg-gray-300 p-3 rounded text-sm font-medium"
            onClick={clear}
          >
            C
          </button>
          <button className="calc-button bg-gray-200 hover:bg-gray-300 p-3 rounded text-sm font-medium">
            ±
          </button>
          <button className="calc-button bg-gray-200 hover:bg-gray-300 p-3 rounded text-sm font-medium">
            %
          </button>
          <button
            className="calc-button bg-orange-500 hover:bg-orange-600 text-white p-3 rounded text-sm font-medium"
            onClick={() => performOperation("÷")}
          >
            ÷
          </button>

          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={() => inputNumber(7)}
          >
            7
          </button>
          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={() => inputNumber(8)}
          >
            8
          </button>
          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={() => inputNumber(9)}
          >
            9
          </button>
          <button
            className="calc-button bg-orange-500 hover:bg-orange-600 text-white p-3 rounded text-sm font-medium"
            onClick={() => performOperation("×")}
          >
            ×
          </button>

          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={() => inputNumber(4)}
          >
            4
          </button>
          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={() => inputNumber(5)}
          >
            5
          </button>
          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={() => inputNumber(6)}
          >
            6
          </button>
          <button
            className="calc-button bg-orange-500 hover:bg-orange-600 text-white p-3 rounded text-sm font-medium"
            onClick={() => performOperation("-")}
          >
            -
          </button>

          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={() => inputNumber(1)}
          >
            1
          </button>
          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={() => inputNumber(2)}
          >
            2
          </button>
          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={() => inputNumber(3)}
          >
            3
          </button>
          <button
            className="calc-button bg-orange-500 hover:bg-orange-600 text-white p-3 rounded text-sm font-medium"
            onClick={() => performOperation("+")}
          >
            +
          </button>

          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium col-span-2"
            onClick={() => inputNumber(0)}
          >
            0
          </button>
          <button
            className="calc-button bg-gray-300 hover:bg-gray-400 p-3 rounded text-sm font-medium"
            onClick={inputDecimal}
          >
            .
          </button>
          <button
            className="calc-button bg-orange-500 hover:bg-orange-600 text-white p-3 rounded text-sm font-medium"
            onClick={handleEquals}
          >
            =
          </button>
        </div>
      </div>
    </div>
  );
};

const SimulatorModal = ({
  selected,
  isOpen,
  onClose,
  mode = "exam", // "exam" or "review"
  selectedAttemptNumber = 1,
  onAttemptNumberChange,
}) => {
  const { userData } = useAuth();
  const userId = userData?._id;

  // Core state - exam mode
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isExamSubmitted, setIsExamSubmitted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [examStartTime, setExamStartTime] = useState(null);
  const [totalPausedTime, setTotalPausedTime] = useState(0);
  const [pauseStartTime, setPauseStartTime] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // FIXED: Add state to track the current attempt number consistently
  const [currentAttemptNumber, setCurrentAttemptNumber] = useState(null);

  // Review mode state
  const [reviewAnswers, setReviewAnswers] = useState({});
  const [availableAttempts, setAvailableAttempts] = useState([]);

  // UI state
  const [uploadedManual, setUploadedManual] = useState(null);
  const [imagePopup, setImagePopup] = useState({
    isOpen: false,
    src: "",
    alt: "",
  });
  const [calculatorVisible, setCalculatorVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const statusId = selected?.statusInfo?._id;
  const saveTimeoutRef = useRef(null);

  // SWR Mutation for saving progress (exam mode only)
  const { trigger: saveProgress } = useSWRMutation(
    statusId && mode === "exam" ? modelTestQuestionUrls.exam(statusId) : null,
    sendPutRequestAuth,
    {
      onSuccess: () => setIsSaving(false),
      onError: () => setIsSaving(false),
    }
  );

  // SWR for loading existing progress/attempt data
  const { data: existingProgress, isLoading: isLoadingProgress } = useSWR(
    statusId ? modelTestQuestionUrls.status(statusId) : null
  );

  // Load attempt data for review mode
  useEffect(() => {
    if (!isOpen || mode !== "review" || !existingProgress?.success) return;

    const data = existingProgress.data;
    console.log("Review mode - full data:", data);

    // Get attempts from attemptScores array
    if (data?.attemptScores && data.attemptScores.length > 0) {
      const attempts = data.attemptScores
        .map((attempt) => attempt.attemptNumber)
        .sort((a, b) => a - b);

      console.log("Available attempts from attemptScores:", attempts);
      setAvailableAttempts(attempts);

      // Auto-select first attempt if none selected, or validate current selection
      const targetAttempt =
        selectedAttemptNumber && attempts.includes(selectedAttemptNumber)
          ? selectedAttemptNumber
          : attempts[attempts.length - 1]; // Default to latest attempt

      if (targetAttempt !== selectedAttemptNumber) {
        console.log("Setting attempt to:", targetAttempt);
        onAttemptNumberChange(targetAttempt);
        return; // Let the effect re-run with the new attempt number
      }
    } else {
      console.log("No attemptScores found");
      setAvailableAttempts([]);
      setReviewAnswers({});
      return;
    }

    // Load answers for the selected attempt
    if (
      selectedAttemptNumber &&
      data.answers &&
      availableAttempts.includes(selectedAttemptNumber)
    ) {
      console.log(`Loading answers for attempt #${selectedAttemptNumber}`);
      console.log("All answers:", data.answers);

      const attemptAnswers = {};
      const filteredAnswers = data.answers.filter(
        (answer) => answer.attemptNumber === selectedAttemptNumber
      );

      console.log("Filtered answers for attempt:", filteredAnswers);

      filteredAnswers.forEach((answer) => {
        const questionIndex = selected?.questions?.findIndex(
          (q) => (q.id || q._id) === answer.question
        );
        if (questionIndex !== -1 && answer.selectedOptionIndex) {
          attemptAnswers[questionIndex] = parseInt(answer.selectedOptionIndex);
        }
      });

      console.log(
        `Final answers for attempt #${selectedAttemptNumber}:`,
        attemptAnswers
      );
      setReviewAnswers(attemptAnswers);
    }
  }, [
    mode,
    selectedAttemptNumber,
    existingProgress,
    isOpen,
    selected?.questions,
    availableAttempts.length,
  ]);

  // FIXED: Save function for exam mode - only send current attempt answers
  const saveToBackend = async (
    currentIndex = currentQuestionIndex,
    currentAnswers = selectedAnswers,
    overrideStatus = null
  ) => {
    if (
      !statusId ||
      !userId ||
      isExamSubmitted ||
      !isInitialized ||
      mode !== "exam" ||
      !currentAttemptNumber
    )
      return;

    try {
      setIsSaving(true);
      const currentTime = new Date();

      let timeTakenMs = 0;
      if (examStartTime) {
        const currentPausedTime =
          isPaused && pauseStartTime ? currentTime - pauseStartTime : 0;
        timeTakenMs =
          currentTime - examStartTime - totalPausedTime - currentPausedTime;
      }

      // FIXED: Only include answers for current attempt to reduce payload size
      const currentAttemptAnswers = Object.keys(currentAnswers).map(
        (index) => ({
          attemptNumber: currentAttemptNumber,
          question:
            selected?.questions?.[index]?.id ||
            selected?.questions?.[index]?._id,
          selectedOptionIndex: currentAnswers[index]?.toString(),
          isCorrect:
            currentAnswers[index] ===
            parseInt(selected?.questions?.[index]?.correctOptionIndex),
        })
      );

      let status = overrideStatus || (isPaused ? "paused" : "in-progress");
      let pausedAt = null;

      if (status === "paused") {
        pausedAt = currentTime.toISOString();
      }

      // FIXED: Send minimal payload - only current attempt data
      await saveProgress({
        user: userId,
        modelTest: selected._id,
        status,
        currentQuestionIndex: currentIndex,
        currentAttempt: currentAttemptNumber,
        currentAttemptAnswers, // FIXED: Send only current attempt answers
        attempts: selected?.statusInfo?.attempts || 0,
        score: 0,
        timeTaken: Math.floor(Math.max(0, timeTakenMs) / 1000),
        remainingTime: timeRemaining,
        startedAt: examStartTime?.toISOString(),
        pausedAt,
        completedAt: null,
      });
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // FIXED: Initialize exam mode - set currentAttemptNumber properly
  useEffect(() => {
    if (!isOpen || mode !== "exam" || isInitialized) return;
    if (isLoadingProgress) return;

    if (existingProgress?.success && existingProgress?.data) {
      const data = existingProgress.data;

      // Check if current attempt is in progress
      const isCurrentAttemptInProgress =
        data.status === "in-progress" || data.status === "paused";

      if (isCurrentAttemptInProgress) {
        // FIXED: Set the current attempt number from the existing data
        setCurrentAttemptNumber(data.currentAttempt);

        // Load existing progress for current attempt
        setCurrentQuestionIndex(data.currentQuestionIndex || 0);

        // Restore current attempt answers
        const answersObj = {};
        if (data.answers && Array.isArray(data.answers)) {
          data.answers
            .filter((answer) => answer.attemptNumber === data.currentAttempt)
            .forEach((answer) => {
              const questionIndex = selected?.questions?.findIndex(
                (q) => (q.id || q._id) === answer.question
              );
              if (questionIndex !== -1 && answer.selectedOptionIndex) {
                answersObj[questionIndex] = parseInt(
                  answer.selectedOptionIndex
                );
              }
            });
        }

        setSelectedAnswers(answersObj);
        setIsExamSubmitted(false);

        const isPausedState = data.status === "paused";
        setIsPaused(isPausedState);

        if (data.startedAt) {
          const startTime = new Date(data.startedAt);
          setExamStartTime(startTime);

          if (data.remainingTime !== undefined && data.remainingTime > 0) {
            setTimeRemaining(data.remainingTime);
          } else {
            const parts = selected.duration.split(":").map(Number);
            let totalSeconds = 0;
            if (parts.length === 2) {
              totalSeconds = parts[0] * 3600 + parts[1] * 60;
            } else if (parts.length === 3) {
              totalSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
            }

            const elapsedSeconds = data.timeTaken || 0;
            const remaining = Math.max(0, totalSeconds - elapsedSeconds);
            setTimeRemaining(remaining);
          }

          if (isPausedState && data.pausedAt) {
            setPauseStartTime(new Date(data.pausedAt));
            setTotalPausedTime((data.timeTaken || 0) * 1000);
          } else if (data.timeTaken) {
            setTotalPausedTime((data.timeTaken || 0) * 1000);
          }
        }
      } else {
        // Start fresh exam - previous attempt was completed
        initializeNewExam();
      }
    } else {
      initializeNewExam();
    }

    setIsInitialized(true);
  }, [isOpen, mode, existingProgress, isLoadingProgress, selected?.duration]);

  const initializeNewExam = () => {
    if (!selected?.duration) return;

    const parts = selected.duration.split(":").map(Number);
    let totalSeconds = 0;

    if (parts.length === 2) {
      totalSeconds = parts[0] * 3600 + parts[1] * 60;
    } else if (parts.length === 3) {
      totalSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    }

    // FIXED: Set currentAttemptNumber for new exam
    const nextAttemptNumber = (selected?.statusInfo?.attempts || 0) + 1;
    setCurrentAttemptNumber(nextAttemptNumber);

    setTimeRemaining(totalSeconds);
    setExamStartTime(new Date());
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setIsExamSubmitted(false);
    setIsPaused(false);
    setTotalPausedTime(0);
    setPauseStartTime(null);
  };

  // Timer for exam mode only
  useEffect(() => {
    if (
      !isOpen ||
      mode !== "exam" ||
      timeRemaining <= 0 ||
      isExamSubmitted ||
      isPaused
    )
      return;

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, mode, timeRemaining, isExamSubmitted, isPaused]);

  // Auto-save for exam mode only
  useEffect(() => {
    if (
      !isInitialized ||
      mode !== "exam" ||
      isExamSubmitted ||
      isPaused ||
      !examStartTime ||
      !currentAttemptNumber // FIXED: Ensure we have currentAttemptNumber before auto-saving
    )
      return;

    const autoSaveInterval = setInterval(() => {
      saveToBackend();
    }, 10000);

    return () => clearInterval(autoSaveInterval);
  }, [
    isInitialized,
    mode,
    isExamSubmitted,
    isPaused,
    examStartTime,
    currentQuestionIndex,
    selectedAnswers,
    timeRemaining,
    currentAttemptNumber, // FIXED: Include in dependency array
  ]);

  // Event handlers for exam mode
  const handleOptionSelect = (optionIndex) => {
    if (mode !== "exam" || isExamSubmitted || isPaused) return;

    const newAnswers = {
      ...selectedAnswers,
      [currentQuestionIndex]: optionIndex,
    };

    setSelectedAnswers(newAnswers);
    saveToBackend(currentQuestionIndex, newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < (selected?.questions?.length || 0) - 1) {
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      if (mode === "exam") {
        saveToBackend(newIndex, selectedAnswers);
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const newIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(newIndex);
      if (mode === "exam") {
        saveToBackend(newIndex, selectedAnswers);
      }
    }
  };

  const handleQuestionJump = (questionIndex) => {
    setCurrentQuestionIndex(questionIndex);
    if (mode === "exam") {
      saveToBackend(questionIndex, selectedAnswers);
    }
  };

  const handlePauseResume = async () => {
    if (mode !== "exam") return;

    const currentTime = new Date();

    if (isPaused) {
      if (pauseStartTime) {
        const pauseDuration = currentTime - pauseStartTime;
        setTotalPausedTime((prev) => prev + pauseDuration);
      }
      setIsPaused(false);
      setPauseStartTime(null);
      await saveToBackend(currentQuestionIndex, selectedAnswers, "in-progress");
    } else {
      setIsPaused(true);
      setPauseStartTime(currentTime);
      await saveToBackend(currentQuestionIndex, selectedAnswers, "paused");
    }
  };

  // FIXED: Submit exam handler - send only current attempt data
  const handleSubmitExam = async () => {
    if (mode !== "exam" || !currentAttemptNumber) return;

    setIsExamSubmitted(true);
    setIsPaused(false);

    if (!statusId || !userId || !selected?._id) return;

    const currentTime = new Date();
    const timeTakenMs = examStartTime
      ? currentTime - examStartTime - totalPausedTime
      : 0;

    let correct = 0;
    // FIXED: Only create answers for current attempt
    const currentAttemptAnswers = Object.keys(selectedAnswers).map((index) => {
      const isCorrect =
        selectedAnswers[index] ===
        parseInt(selected?.questions?.[index]?.correctOptionIndex);
      if (isCorrect) correct++;

      return {
        attemptNumber: currentAttemptNumber,
        question:
          selected?.questions?.[index]?.id || selected?.questions?.[index]?._id,
        selectedOptionIndex: selectedAnswers[index]?.toString(),
        isCorrect,
      };
    });

    const timeTakenSeconds = Math.floor(Math.max(0, timeTakenMs) / 1000);

    try {
      setIsSaving(true);

      // FIXED: Create attempt score for current attempt only
      const currentAttemptScore = {
        attemptNumber: currentAttemptNumber,
        score: correct,
        timeTaken: timeTakenSeconds,
        completedAt: currentTime.toISOString(),
      };

      // FIXED: Send optimized payload for submission
      await saveProgress({
        user: userId,
        modelTest: selected._id,
        status: "completed",
        currentQuestionIndex,
        currentAttempt: currentAttemptNumber,
        currentAttemptAnswers, // FIXED: Only current attempt answers
        currentAttemptScore, // FIXED: Only current attempt score
        attempts: currentAttemptNumber, // This attempt is now completed
        score: correct,
        timeTaken: timeTakenSeconds,
        remainingTime: timeRemaining,
        startedAt: examStartTime?.toISOString(),
        pausedAt: null,
        completedAt: currentTime.toISOString(),
      });
      // setCurrentQuestionIndex(0);
    } catch (error) {
      console.error("Error submitting exam:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${mins
        .toString()
        .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const calculateScore = () => {
    const answers = mode === "review" ? reviewAnswers : selectedAnswers;
    const showResults = mode === "review" || isExamSubmitted;

    if (!showResults) {
      return {
        correct: 0,
        wrong: 0,
        unanswered: selected?.questions?.length || 0,
      };
    }

    let correct = 0,
      wrong = 0,
      unanswered = 0;
    selected?.questions?.forEach((question, index) => {
      const userAnswer = answers[index];
      if (userAnswer === undefined) {
        unanswered++;
      } else if (userAnswer === parseInt(question.correctOptionIndex)) {
        correct++;
      } else {
        wrong++;
      }
    });

    return { correct, wrong, unanswered };
  };

  const getOptionClass = (optionIndex) => {
    const currentQuestion = selected?.questions?.[currentQuestionIndex];
    const answers = mode === "review" ? reviewAnswers : selectedAnswers;
    const selectedOption = answers[currentQuestionIndex];
    const showResults = mode === "review" || isExamSubmitted;

    if (showResults) {
      const correctIndex = parseInt(currentQuestion?.correctOptionIndex);
      if (optionIndex === correctIndex) return "bg-green-100 border-green-500";
      if (optionIndex === selectedOption && optionIndex !== correctIndex)
        return "bg-red-100 border-red-500";
      return "bg-gray-50";
    }

    if (mode === "exam") {
      return selectedOption === optionIndex
        ? "bg-blue-100 border-blue-500"
        : "bg-white hover:bg-gray-50";
    }

    return "bg-gray-50"; // Review mode default
  };

  const getQuestionStatus = (questionIndex) => {
    const answers = mode === "review" ? reviewAnswers : selectedAnswers;
    const showResults = mode === "review" || isExamSubmitted;

    if (showResults) {
      const userAnswer = answers[questionIndex];
      if (userAnswer !== undefined) {
        const isCorrect =
          userAnswer ===
          parseInt(
            selected?.questions?.[questionIndex]?.correctOptionIndex || -1
          );
        return isCorrect ? "correct" : "incorrect";
      }
      return "unanswered";
    }
    return answers[questionIndex] !== undefined ? "answered" : "unanswered";
  };

  const getPaginationButtonClass = (questionIndex) => {
    const status = getQuestionStatus(questionIndex);
    const isActive = questionIndex === currentQuestionIndex;

    let baseClass =
      "w-8 h-8 rounded-full text-sm font-medium transition-all cursor-pointer flex items-center justify-center ";

    if (isActive) baseClass += "ring-2 ring-blue-500 ring-offset-2 ";

    if (mode === "review" || isExamSubmitted) {
      switch (status) {
        case "correct":
          return baseClass + "bg-green-500 text-white hover:bg-green-600";
        case "incorrect":
          return baseClass + "bg-red-500 text-white hover:bg-red-600";
        default:
          return baseClass + "bg-gray-200 text-gray-700 hover:bg-gray-300";
      }
    }

    return status === "answered"
      ? baseClass + "bg-blue-500 text-white hover:bg-blue-600"
      : baseClass + "bg-gray-200 text-gray-700 hover:bg-gray-300";
  };

  const handleImageClick = (src, alt) => {
    setImagePopup({ isOpen: true, src, alt });
  };

  const closeImagePopup = () => {
    setImagePopup({ isOpen: false, src: "", alt: "" });
  };

  const handleManualUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === "application/pdf") {
      const fileURL = URL.createObjectURL(file);
      setUploadedManual({ name: file.name, url: fileURL });
    } else {
      alert("Please upload a PDF file only");
    }
  };

  const removeManual = () => {
    if (uploadedManual?.url) {
      URL.revokeObjectURL(uploadedManual.url);
    }
    setUploadedManual(null);
  };

  const resetSimulator = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setReviewAnswers({});
    setAvailableAttempts([]);
    setIsExamSubmitted(false);
    setIsPaused(false);
    setTimeRemaining(0);
    setCalculatorVisible(false);
    setExamStartTime(null);
    setTotalPausedTime(0);
    setPauseStartTime(null);
    setIsSaving(false);
    setIsInitialized(false);
    setCurrentAttemptNumber(null); // FIXED: Reset currentAttemptNumber
    removeManual();
  };

  const handleClose = () => {
    resetSimulator();
    onClose();
  };

  // Get attempt info for review mode
  const getAttemptInfo = () => {
    if (mode !== "review" || !existingProgress?.data?.attemptScores)
      return null;

    const attemptInfo = existingProgress.data.attemptScores.find(
      (attempt) => attempt.attemptNumber === selectedAttemptNumber
    );

    console.log("Found attempt info:", attemptInfo);
    return attemptInfo;
  };

  // Component data
  const questions = selected?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const answers = mode === "review" ? reviewAnswers : selectedAnswers;
  const selectedOption = answers[currentQuestionIndex];
  const { correct, wrong, unanswered } = calculateScore();
  const attemptInfo = getAttemptInfo();

  if (!isOpen) return null;

  if (isLoadingProgress && mode === "exam") {
    return (
      <Modal
        isOpen={isOpen}
        title="Loading Exam..."
        footer={null}
        onCancel={handleClose}
        formWidth="max-w-md"
        formHeight="max-h-64"
      >
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3">Loading your exam progress...</span>
        </div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        title={`${selected?.title || "Simulator"} - ${
          mode === "review"
            ? `Review Attempt #${selectedAttemptNumber}`
            : `Question ${currentQuestionIndex + 1}`
        }`}
        footer={null}
        onCancel={handleClose}
        formWidth="max-w-[98%]"
        formHeight="max-h-[90vh]"
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center bg-blue-50 px-6 py-3 border-b">
            <div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  {mode === "exam" ? (
                    <>
                      <div className="flex items-center gap-2 text-gray-600">
                        <FontAwesomeIcon icon={faClock} />
                        <span
                          className={`font-mono ${
                            timeRemaining < 300 ? "text-red-600 font-bold" : ""
                          } ${isPaused ? "text-orange-600" : ""}`}
                        >
                          {formatTime(timeRemaining)} {isPaused && "(PAUSED)"}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Question {currentQuestionIndex + 1} of{" "}
                        {questions.length}
                      </div>
                      <div className="text-sm text-blue-600">
                        Attempt #{currentAttemptNumber || 1}{" "}
                        {/* FIXED: Use currentAttemptNumber */}
                      </div>
                      {isSaving && (
                        <div className="flex items-center gap-1 text-xs text-blue-600">
                          <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-500"></div>
                          <span>Saving...</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-gray-500">
                        Question {currentQuestionIndex + 1} of{" "}
                        {questions.length}
                      </div>
                      {attemptInfo && (
                        <div className="flex gap-4 text-sm">
                          <span className="text-blue-600">
                            Score: {attemptInfo.score}/{questions.length}
                          </span>
                          <span className="text-gray-600">
                            Time: {formatTime(attemptInfo.timeTaken)}
                          </span>
                          <span className="text-gray-600">
                            Completed:{" "}
                            {new Date(
                              attemptInfo.completedAt
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Score display */}
              {(mode === "review" || isExamSubmitted) && (
                <div className="text-sm font-medium mt-2">
                  <span className="text-green-600">Correct: {correct}</span>
                  <span className="mx-2 text-gray-400">|</span>
                  <span className="text-red-600">Wrong: {wrong}</span>
                  <span className="mx-2 text-gray-400">|</span>
                  <span className="text-gray-600">
                    Unanswered: {unanswered}
                  </span>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center gap-4">
              {/* Review mode: Attempt selector */}
              {mode === "review" && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">View Attempt:</span>
                  {availableAttempts.length > 0 ? (
                    <select
                      value={selectedAttemptNumber || availableAttempts[0]}
                      onChange={(e) => {
                        const attemptNum = parseInt(e.target.value);
                        console.log("Switching to attempt:", attemptNum);
                        onAttemptNumberChange(attemptNum);
                      }}
                      className="px-3 py-1 border rounded text-sm bg-white min-w-[120px]"
                    >
                      {availableAttempts.map((attemptNum) => {
                        const attemptInfo =
                          existingProgress?.data?.attemptScores?.find(
                            (a) => a.attemptNumber === attemptNum
                          );
                        return (
                          <option key={attemptNum} value={attemptNum}>
                            Attempt #{attemptNum}{" "}
                            {attemptInfo &&
                              `(${attemptInfo.score}/${questions.length})`}
                          </option>
                        );
                      })}
                    </select>
                  ) : (
                    <span className="text-sm text-gray-500">
                      No completed attempts found
                    </span>
                  )}
                </div>
              )}

              {/* Exam mode controls */}
              {mode === "exam" && !isExamSubmitted && (
                <button
                  className={`${
                    isPaused
                      ? "text-green-600 hover:text-green-800"
                      : "text-orange-600 hover:text-orange-800"
                  } flex items-center gap-2 text-sm`}
                  onClick={handlePauseResume}
                  disabled={isSaving}
                >
                  <FontAwesomeIcon icon={isPaused ? faPlay : faPause} />
                  {isPaused ? "Resume" : "Pause"}
                </button>
              )}

              <button
                className="text-blue-600 hover:text-blue-800 flex items-center gap-2 text-sm"
                onClick={() => setCalculatorVisible(!calculatorVisible)}
              >
                <FontAwesomeIcon icon={faCalculator} />
                Calculator
              </button>

              <button className="text-red-600 hover:text-red-800 flex items-center gap-2 text-sm">
                <FontAwesomeIcon icon={faFlag} />
                Report Issue
              </button>

              {mode === "exam" && !isExamSubmitted && (
                <Button
                  onClick={handleSubmitExam}
                  variant="danger"
                  size="small"
                  disabled={
                    Object.keys(selectedAnswers).length === 0 ||
                    isPaused ||
                    isSaving
                  }
                >
                  {isSaving ? "Saving..." : "Submit Exam"}
                </Button>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="px-3 py-3 flex justify-between items-center my-2">
            <div className="flex items-center justify-center">
              <div className="flex items-center gap-4 flex-wrap">
                {questions.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuestionJump(index)}
                    className={getPaginationButtonClass(index)}
                    title={`Question ${index + 1} - ${getQuestionStatus(
                      index
                    )}`}
                    disabled={mode === "exam" && isPaused}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center gap-3">
              <Button
                onClick={handlePrevious}
                disabled={
                  currentQuestionIndex === 0 || (mode === "exam" && isPaused)
                }
                variant="outline"
                size="small"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
                Previous
              </Button>

              <Button
                onClick={handleNext}
                disabled={
                  currentQuestionIndex === questions.length - 1 ||
                  (mode === "exam" && isPaused)
                }
                variant="outline"
                size="small"
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} className="ml-2" />
              </Button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex relative">
            {/* Pause Overlay - only for exam mode */}
            {mode === "exam" && isPaused && (
              <div className="absolute inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center">
                <div className="bg-white rounded-lg p-8 shadow-2xl text-center">
                  <FontAwesomeIcon
                    icon={faPause}
                    className="text-4xl text-orange-500 mb-4"
                  />
                  <h3 className="text-xl font-bold mb-2">Exam Paused</h3>
                  <p className="text-gray-600 mb-4">
                    Click Resume to continue your exam
                  </p>
                  <Button
                    onClick={handlePauseResume}
                    variant="primary"
                    disabled={isSaving}
                  >
                    <FontAwesomeIcon icon={faPlay} className="mr-2" />
                    {isSaving ? "Saving..." : "Resume Exam"}
                  </Button>
                </div>
              </div>
            )}

            {/* Question Section */}
            <div className="flex-1 p-3">
              <div className="max-w-5xl">
                <div
                  className="mb-3 text-gray-800"
                  dangerouslySetInnerHTML={{
                    __html: currentQuestion?.questionText || "",
                  }}
                />

                {currentQuestion?.questionImage && (
                  <div className="mb-3">
                    <img
                      src={currentQuestion.questionImage}
                      alt="Question"
                      className="w-[850px] h-auto rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() =>
                        handleImageClick(
                          currentQuestion.questionImage,
                          "Question Image"
                        )
                      }
                    />
                  </div>
                )}

                <div className="mb-6 mt-3">
                  <div className="grid grid-cols-2 gap-4">
                    {currentQuestion?.options?.map((option, index) => (
                      <label
                        key={index}
                        className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${getOptionClass(
                          index + 1
                        )} ${
                          (mode === "exam" && isPaused) || mode === "review"
                            ? "cursor-not-allowed"
                            : ""
                        }`}
                        onClick={() =>
                          mode === "exam" &&
                          !isExamSubmitted &&
                          !isPaused &&
                          handleOptionSelect(index + 1)
                        }
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            name={`question_${currentQuestionIndex}`}
                            checked={selectedOption === index + 1}
                            onChange={() =>
                              mode === "exam" &&
                              !isExamSubmitted &&
                              !isPaused &&
                              handleOptionSelect(index + 1)
                            }
                            disabled={
                              mode === "review" ||
                              isExamSubmitted ||
                              (mode === "exam" && isPaused)
                            }
                            className="mr-3"
                          />
                          <span className="font-medium mr-2">
                            {String.fromCharCode(65 + index)}.
                          </span>
                          <div className="flex flex-col gap-2 flex-1">
                            {/* Option images */}
                            {index === 0 && currentQuestion?.optionAImage && (
                              <img
                                src={currentQuestion.optionAImage}
                                alt="Option A"
                                className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(
                                    currentQuestion.optionAImage,
                                    "Option A"
                                  );
                                }}
                              />
                            )}
                            {index === 1 && currentQuestion?.optionBImage && (
                              <img
                                src={currentQuestion.optionBImage}
                                alt="Option B"
                                className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(
                                    currentQuestion.optionBImage,
                                    "Option B"
                                  );
                                }}
                              />
                            )}
                            {index === 2 && currentQuestion?.optionCImage && (
                              <img
                                src={currentQuestion.optionCImage}
                                alt="Option C"
                                className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(
                                    currentQuestion.optionCImage,
                                    "Option C"
                                  );
                                }}
                              />
                            )}
                            {index === 3 && currentQuestion?.optionDImage && (
                              <img
                                src={currentQuestion.optionDImage}
                                alt="Option D"
                                className="max-w-[250px] max-h-[200px] w-auto h-auto object-contain rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleImageClick(
                                    currentQuestion.optionDImage,
                                    "Option D"
                                  );
                                }}
                              />
                            )}
                            <div className="flex items-center justify-between">
                              <span>{option}</span>
                              {/* Show indicators in review mode */}
                              {mode === "review" && (
                                <div className="flex items-center gap-2 ml-2">
                                  {selectedOption === index + 1 && (
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                      Your Answer
                                    </span>
                                  )}
                                  {parseInt(
                                    currentQuestion?.correctOptionIndex
                                  ) ===
                                    index + 1 && (
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                      Correct
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Manual/Solution Section */}
            <div
              className={`${
                uploadedManual && "min-h-[62vh]"
              } w-[50%] bg-gray-50 border-l`}
            >
              <div className="p-3 h-full flex flex-col">
                {mode === "exam" && !isExamSubmitted ? (
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-2 min-h-[40px]">
                      <h3 className="text-lg font-semibold">Manual</h3>
                      {uploadedManual && (
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon
                            icon={faFileAlt}
                            className="text-red-500 text-sm"
                          />
                          <span
                            className="text-xs font-medium truncate max-w-[120px]"
                            title={uploadedManual.name}
                          >
                            {uploadedManual.name}
                          </span>
                          <button
                            onClick={removeManual}
                            className="text-red-500 hover:text-red-700 ml-1"
                            title="Remove manual"
                          >
                            <FontAwesomeIcon icon={faTrash} size="sm" />
                          </button>
                        </div>
                      )}
                    </div>

                    {!uploadedManual ? (
                      <div className="flex-1 flex items-center justify-center">
                        <div className="w-full">
                          <input
                            type="file"
                            accept=".pdf"
                            onChange={handleManualUpload}
                            className="hidden"
                            id="manual-upload"
                          />
                          <label
                            htmlFor="manual-upload"
                            className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-blue-500 transition-colors"
                          >
                            <FontAwesomeIcon
                              icon={faUpload}
                              className="text-2xl text-gray-400 mb-2"
                            />
                            <span className="text-gray-600 text-center text-sm">
                              Click to upload manual (PDF only)
                            </span>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 border border-gray-200 rounded-lg overflow-hidden">
                        {/* <iframe
                          src={uploadedManual.url}
                          className="w-full h-full block"
                          title="Manual PDF"
                        /> */}
                        <PDFViewer uploadedManual={uploadedManual} />
                      </div>
                    )}
                  </div>
                ) : (
                  // Solution section - for completed exam or review mode
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold">Solution</h3>
                    </div>

                    <div className="mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg mb-2">
                        <span className="font-semibold text-blue-800">
                          Detailed Solution
                        </span>
                      </div>

                      {currentQuestion?.solutionText && (
                        <div
                          className="mb-4 text-gray-700"
                          dangerouslySetInnerHTML={{
                            __html: currentQuestion.solutionText,
                          }}
                        />
                      )}

                      {currentQuestion?.solutionImage &&
                        currentQuestion.solutionImage.length > 0 && (
                          <div className="flex flex-col gap-3 mb-4">
                            {currentQuestion.solutionImage.map(
                              (item, index) => (
                                <img
                                  key={index}
                                  src={item}
                                  alt="Solution"
                                  className="w-full h-auto rounded-lg shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={() =>
                                    handleImageClick(item, "Solution Image")
                                  }
                                />
                              )
                            )}
                          </div>
                        )}

                      <div className="text-sm">
                        <div className="font-medium mb-2">Answer Analysis:</div>
                        <div className="bg-gray-100 p-3 rounded-lg space-y-2">
                          <div className="flex justify-between items-center">
                            <span>Correct Answer:</span>
                            <span className="font-medium text-green-600">
                              (
                              {String.fromCharCode(
                                65 +
                                  parseInt(
                                    currentQuestion?.correctOptionIndex - 1
                                  )
                              )}
                              )
                              {
                                currentQuestion?.options?.[
                                  parseInt(
                                    currentQuestion?.correctOptionIndex
                                  ) - 1
                                ]
                              }
                            </span>
                          </div>

                          {/* Show user's answer in review mode or after submission */}
                          {(mode === "review" || isExamSubmitted) && (
                            <div className="flex justify-between items-center">
                              <span>Your Answer:</span>
                              {selectedOption !== undefined ? (
                                <span
                                  className={`font-medium ${
                                    selectedOption ===
                                    parseInt(
                                      currentQuestion?.correctOptionIndex
                                    )
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  (
                                  {String.fromCharCode(65 + selectedOption - 1)}
                                  )
                                  {
                                    currentQuestion?.options?.[
                                      selectedOption - 1
                                    ]
                                  }
                                </span>
                              ) : (
                                <span className="text-gray-500 italic">
                                  Not answered
                                </span>
                              )}
                            </div>
                          )}

                          {/* Show result */}
                          {(mode === "review" || isExamSubmitted) && (
                            <div className="flex justify-between items-center pt-2 border-t">
                              <span>Result:</span>
                              <span
                                className={`font-bold ${
                                  selectedOption ===
                                  parseInt(currentQuestion?.correctOptionIndex)
                                    ? "text-green-600"
                                    : selectedOption !== undefined
                                    ? "text-red-600"
                                    : "text-gray-500"
                                }`}
                              >
                                {selectedOption ===
                                parseInt(currentQuestion?.correctOptionIndex)
                                  ? "✓ Correct"
                                  : selectedOption !== undefined
                                  ? "✗ Incorrect"
                                  : "Not Answered"}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <FloatingCalculator
        isVisible={calculatorVisible}
        onClose={() => setCalculatorVisible(false)}
      />

      {imagePopup.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[9999]">
          <div className="relative max-w-[90vw] max-h-[90vh] p-4">
            <button
              onClick={closeImagePopup}
              className="absolute top-[-8px] right-[-8px] bg-white text-primary rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-80 transition-all z-10"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>
            <img
              src={imagePopup.src}
              alt={imagePopup.alt}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
            <div className="text-white text-center mt-2 text-sm">
              {imagePopup.alt}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SimulatorModal;
